import { useState, useEffect } from 'react';
import { auth, onAuthStateChanged } from '../firebase';
import useApiClient from './useApiClient';

const useUserRole = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [shopAccount, setShopAccount] = useState(null);
  const [error, setError] = useState(null);
  const apiClient = useApiClient();

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUser(null);
        setUserRole(null);
        setUserPermissions(null);
        setPermissionGroups([]);
        setPaymentStatus(null);
        setShopAccount(null);
        setError(null);
        // Clear sessionId cookie when user signs out or is missing
        try {
          if (typeof window !== 'undefined' && typeof document !== 'undefined') {
            const hostname = window.location.hostname || '';
            const domainAttr = hostname.endsWith('servicegest.ro') ? '; Domain=.servicegest.ro' : (hostname === 'storage.servicegest.ro' ? '; Domain=storage.servicegest.ro' : '');
            const secureAttr = window.location.protocol === 'https:' ? '; Secure; SameSite=Lax' : '; SameSite=Lax';
            document.cookie = `sessionId=; Path=/${domainAttr}; Max-Age=0${secureAttr}`;
          }
        } catch (e) {
          // no-op
        }
        setLoading(false);
        return;
      }

      setUser(user);
      setLoading(true);
      setError(null);

      try {
        const roleData = await apiClient.get('/user/role');
        console.log('Role data received:', roleData);
        console.log('Payment status:', roleData?.shopAccount?.paymentStatus);
        setUserRole(roleData.role);
        setUserPermissions(roleData.permissions);
        // Extract permission groups from permissionByGroup keys - only include groups with at least one permission
        const permissionByGroup = roleData?.shopAccount?.permissionByGroup;
        const groups = roleData?.permissionGroups || 
          roleData?.shopAccount?.permissionGroups || 
          (permissionByGroup ? Object.keys(permissionByGroup).filter(key => 
            Array.isArray(permissionByGroup[key]) && permissionByGroup[key].length > 0
          ) : []);
        console.log('Permission groups extracted:', groups);
        setPermissionGroups(groups);
        setPaymentStatus(roleData?.shopAccount?.paymentStatus);
        setShopAccount(roleData?.shopAccount);

        // Set sessionId cookie for storage access if provided by backend
        const sessionId = roleData?.sessionId
        if (sessionId) {
          try {
            if (typeof window !== 'undefined' && typeof document !== 'undefined') {
              const hostname = window.location.hostname || '';
              const domainAttr = hostname.endsWith('servicegest.ro') ? '; Domain=.servicegest.ro' : (hostname === 'storage.servicegest.ro' ? '; Domain=storage.servicegest.ro' : '');
              const secureAttr = window.location.protocol === 'https:' ? '; Secure; SameSite=Lax' : '; SameSite=Lax';
              const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString(); // 1 day
              document.cookie = `sessionId=${encodeURIComponent(sessionId)}; Expires=${expires}${domainAttr}; Path=/${secureAttr}`;
            }
          } catch (e) {
            // ignore cookie errors
          }
        }
      } catch (err) {
        console.error('Error fetching user role:', err);
        setError(err);
        setUserRole(null);
        setUserPermissions(null);
        setPermissionGroups([]);
        setPaymentStatus(null);
        setShopAccount(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, [apiClient]);

  return {
    loading,
    user,
    userRole,
    userPermissions,
    permissionGroups,
    paymentStatus,
    shopAccount,
    error
  };
};

export default useUserRole;
